import { t } from "@lingui/macro";
import {
  Book,
  CircleNotch,
  FileDoc,
  FilePdf,
  GlobeSimple,
  LinkedinLogo,
  Upload,
} from "@phosphor-icons/react";
import {
  LinkedInParser,
} from "@reactive-resume/parser";
import { ResumeData, resumeDataSchema } from "@reactive-resume/schema";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  Label,
  Separator,
  Switch,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { saveAs } from "file-saver";
import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { useFeatureGate } from "@/client/hooks/use-feature-gate";
import { useGenerateDocx } from "@/client/services/resume/docx";
import { usePrintResume } from "@/client/services/resume/print";
import { useResumeStore } from "@/client/stores/resume";
import { canExportResume } from "@reactive-resume/utils";

export const ExportSection = () => {
  const { toast } = useToast();
  const { printResume, loading } = usePrintResume();
  const { generateDocx, loading: docxLoading } = useGenerateDocx();
  const jsonInputRef = useRef<HTMLInputElement>(null);
  const linkedinInputRef = useRef<HTMLInputElement>(null);
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const setValue = useResumeStore((state) => state.setValue);
  const isPublic = useResumeStore((state) => state.resume.visibility === "public");

  const { gate, planTier, requirePlan } = useFeatureGate();
  const createdAt = useResumeStore((state) => state.resume.createdAt);

  const onPdfExport = async () => {
    if (!canExportResume(planTier, "pdf-export", createdAt) && !gate("pdf-export")) return;
    const { resume } = useResumeStore.getState();
    const { url } = await printResume({ id: resume.id });

    const win = window.open(url, "_blank");
    if (win) win.focus();
  };

  const onDocxExport = async () => {
    if (!canExportResume(planTier, "docx-export", createdAt) && !gate("docx-export")) return;
    const { resume } = useResumeStore.getState();
    await generateDocx({ id: resume.id });
  };

  const onJsonExport = () => {
    if (!canExportResume(planTier, "json-export", createdAt) && !gate("json-export")) return;
    const { resume } = useResumeStore.getState();
    const filename = `careerbldr_resume-${resume.id}.json`;
    const resumeJSON = JSON.stringify(resume.data, null, 2);

    saveAs(new Blob([resumeJSON], { type: "application/json" }), filename);
  };

  const onJsonImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!requirePlan(t`Subscribe to import resume data.`)) {
      if (jsonInputRef.current) jsonInputRef.current.value = "";
      return;
    }
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        const result = resumeDataSchema.safeParse(json);

        if (result.success) {
          const data = result.data as ResumeData;
          setValue("metadata.layout", data.metadata.layout);
          setValue("basics", data.basics);
          setValue("sections", data.sections);
          setValue("metadata", data.metadata);

          toast({
            variant: "success",
            title: t`Resume imported successfully`,
          });
        } else {
          toast({
            variant: "error",
            title: t`Invalid resume file`,
            description: t`The file does not match the expected format.`,
          });
        }
      } catch {
        toast({
          variant: "error",
          title: t`Failed to import`,
          description: t`Could not parse the JSON file.`,
        });
      }
    };
    reader.readAsText(file);

    if (jsonInputRef.current) jsonInputRef.current.value = "";
  };

  const onLinkedInImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!gate("linkedin-import")) return;
    const file = event.target.files?.[0];
    if (!file) return;

    setLinkedinLoading(true);

    try {
      const parser = new LinkedInParser();
      const raw = await parser.readFile(file);
      const validated = await parser.validate(raw);
      const data = parser.convert(validated);

      setValue("metadata.layout", data.metadata.layout);
      setValue("basics", data.basics);
      setValue("sections", data.sections);

      toast({
        variant: "success",
        title: t`LinkedIn data imported successfully`,
      });
    } catch {
      toast({
        variant: "error",
        title: t`Failed to import LinkedIn data`,
        description: t`Please make sure you uploaded a valid LinkedIn data export (.zip).`,
      });
    } finally {
      setLinkedinLoading(false);
      if (linkedinInputRef.current) linkedinInputRef.current.value = "";
    }
  };

  return (
    <section className="grid w-full min-w-0 max-w-full gap-y-4">
      {/* Download */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t`Download`}
        </Label>

        <Card
          onClick={onPdfExport}
          className={cn(
            "min-w-0 w-full max-w-full cursor-pointer flex-row items-center gap-x-3 border bg-card px-3 py-2.5 transition-colors hover:bg-secondary-accent/60",
            loading && "pointer-events-none opacity-60",
          )}
        >
          {loading ? (
            <CircleNotch size={18} className="shrink-0 animate-spin" />
          ) : (
            <FilePdf size={18} className="shrink-0" />
          )}
          <CardContent className="min-w-0 flex-1 p-0">
            <CardTitle className="text-xs">{t`Download PDF`}</CardTitle>
            <CardDescription className="text-[11px] font-normal leading-tight">
              {!planTier
                ? t`Download the resume you already have. Subscribe to keep editing.`
                : t`Recommended: pixel-perfect match of your resume preview.`}
            </CardDescription>
          </CardContent>
        </Card>

        <Card
          onClick={onDocxExport}
          className={cn(
            "min-w-0 w-full max-w-full cursor-pointer flex-row items-center gap-x-3 border bg-card px-3 py-2.5 transition-colors hover:bg-secondary-accent/60",
            docxLoading && "pointer-events-none opacity-60",
          )}
        >
          {docxLoading ? (
            <CircleNotch size={18} className="shrink-0 animate-spin" />
          ) : (
            <FileDoc size={18} className="shrink-0" />
          )}
          <CardContent className="min-w-0 flex-1 p-0">
            <CardTitle className="text-xs">{t`Download DOCX`}</CardTitle>
            <CardDescription className="text-[11px] font-normal leading-tight">
              {t`Experimental: editable Word document. Layout may differ from the preview for some templates.`}
            </CardDescription>
          </CardContent>
        </Card>

        <Card
          onClick={onJsonExport}
          className="min-w-0 w-full max-w-full cursor-pointer flex-row items-center gap-x-3 border bg-card px-3 py-2.5 transition-colors hover:bg-secondary-accent/60"
        >
          <GlobeSimple size={18} className="shrink-0" />
          <CardContent className="min-w-0 flex-1 p-0">
            <CardTitle className="text-xs">{t`Export JSON`}</CardTitle>
            <CardDescription className="text-[11px] font-normal leading-tight">
              {t`Best for backup or manual editing. Re-import anytime to restore your data.`}
            </CardDescription>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Sharing */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t`Sharing`}
        </Label>

        <div className="flex items-center gap-x-3">
          <Switch
            id="visibility"
            checked={isPublic}
            onCheckedChange={(checked) => {
              setValue("visibility", checked ? "public" : "private");
            }}
          />
          <Label htmlFor="visibility" className="space-y-0.5">
            <p className="text-xs font-medium">{t`Public`}</p>
            <p className="text-[11px] leading-tight text-muted-foreground">
              {t`Anyone with the link can view and download the resume.`}
            </p>
          </Label>
        </div>
      </div>

      <Separator />

      {/* Import */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t`Import`}
        </Label>

        <input
          ref={jsonInputRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={onJsonImport}
        />

        <Card
          onClick={() => jsonInputRef.current?.click()}
          className="min-w-0 w-full max-w-full cursor-pointer flex-row items-center gap-x-3 border bg-card px-3 py-2.5 transition-colors hover:bg-secondary-accent/60"
        >
          <Upload size={18} className="shrink-0" />
          <CardContent className="min-w-0 flex-1 p-0">
            <CardTitle className="text-xs">{t`Import from CareerBldr`}</CardTitle>
            <CardDescription className="text-[11px] font-normal leading-tight">
              {t`Load a previously exported .json file.`}
            </CardDescription>
          </CardContent>
        </Card>

        <input
          ref={linkedinInputRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={onLinkedInImport}
        />

        <Card
          onClick={() => linkedinInputRef.current?.click()}
          className={cn(
            "min-w-0 w-full max-w-full cursor-pointer flex-row items-center gap-x-3 border bg-card px-3 py-2.5 transition-colors hover:bg-secondary-accent/60",
            linkedinLoading && "pointer-events-none opacity-60",
          )}
        >
          {linkedinLoading ? (
            <CircleNotch size={18} className="shrink-0 animate-spin" />
          ) : (
            <LinkedinLogo size={18} className="shrink-0" />
          )}
          <CardContent className="min-w-0 flex-1 p-0">
            <CardTitle className="text-xs">{t`Import from LinkedIn`}</CardTitle>
            <CardDescription className="text-[11px] font-normal leading-tight">
              {t`Upload your LinkedIn data export (.zip).`}
            </CardDescription>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Documentation & Help */}
      <AnimatePresence>
        <motion.a
          href="https://careerbldr.gitbook.io/documentation/overview/welcome"
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="flex items-center gap-x-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          whileHover={{ x: 2 }}
        >
          <Book size={16} />
          <span>{t`Documentation & Help`}</span>
        </motion.a>
      </AnimatePresence>
    </section>
  );
};
